import { useEffect, useState } from 'react'
import * as yup from 'yup'
import { isEmpty } from 'ramda'
import { toast } from 'react-toastify'
import { ApolloError } from '@apollo/client'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm } from 'react-hook-form'
import { Box, Grid, Tooltip, Typography, useMediaQuery } from '@mui/material'
import { useContract } from '~/contexts'
import { Form } from '~/components/Form/Form'
import { Input } from '~/components/Form/Input'
import { uploadToS3 } from '~/services/uploadFile'
import { mapErrorToMessage } from '~/utils/errors'
import { Option, defaultOption } from '~/utils/options'
import { clearString, onlyNumbers, theme, transformMoney } from '~/utils'
import { DragAndDrop } from '~/components/Form/DragAndDrop'
import { addressSchema, fileSchema, requiredFileSchema } from '~/utils/yupSchema'
import { INSTALLATION_NUMBER_ERROR_MESSAGE, INSTALLATION_NUMBER_REGEX } from '~/components/InstallationNumberMigration/installationNumber'
import { ActionButtons, EnergyBillFields } from './components'
import { ContractAnnotations, ContractInfo, PropsWithWizard } from '~/components'
import {
  FileModel,
  ContractModel,
  EStorageService,
  useDealershipListQuery,
  ETariffClassification,
  EConsumerUnitClassification,
  ContractAddEnergyBillInfoGraphqlDto,
  useContractAddEnergyBillInfoMutation,
  useContractUpdateMutation,
  EContractStatus,
  ContractUpdateEnergyBillInfo,
  useContractUpdateEnergyBillInfoMutation,
  EProposalFileType,
  EConsumerUnitTension,
  EProposalFileStatus,
  ContractFileCreateDto,
  ContractFileModel
} from '~/graphql/types'
import { monthsLabel } from '~/components/ContractAverageConsumptionDialog'
import { IconInfoCircle } from '@tabler/icons-react'

type AddressForm = {
  city: string
  state: string
  street: string
  number: string
  zipcode: string
  complement: string
  neighborhood: string
}

export type ConsumerUnitForm = {
  //base info
  name: string
  averageEnergyBillValue: number

  //energy bill info
  tusdValue?: number
  dealershipId: string
  energyTaxes?: number
  AmountCents?: number
  clientNumber: string
  avarageConsumption: string
  distributionTaxes?: number
  installationNumber: string
  invoiceDueDay?: number
  energyBill?: File[] | null
  address: AddressForm | null
  avarageBillAmountCents?: number
  tension: EConsumerUnitTension
  tariffClassification: ETariffClassification
  consumerClassification: EConsumerUnitClassification

  //dealeship login info
  login: string
  password: string
  extraPassword: string
  previousGenerationBalance?: number
  ownGenerationAverageKwh?: number
}

export const ConsumerUnit: React.FC<PropsWithWizard> = (props) => {

  const { contract, setContract, isDisabledContractUpdate, setIsDisabledContractUpdate, setIsUpdatingContract } = useContract()
  const [uploadingFile, setUploadingFile] = useState(false)
  const [updateContractEnergyBillInfo, { loading: updateContractEnergyBillInfoLoading }] = useContractUpdateEnergyBillInfoMutation()
  const [updateContract, { loading: updateContractLoading }] = useContractUpdateMutation()
  const [updateEnergyBillInfo, { loading: updateEnergyBillLoading }] = useContractAddEnergyBillInfoMutation()
  const [filteredDealerships, setFilteredDealerships] = useState<Option[]>([defaultOption])
  const { data: dealerships, loading: loadingDealerships } = useDealershipListQuery({ variables: { params: { pageSize: 99999 } } })
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const isComplete = contract?.status === EContractStatus.sent || contract?.status === EContractStatus.active
  const isFormalized = Boolean(contract?.isFormalizedByPartner)

  const energyBillDocument = contract?.files?.find(file => file.type === EProposalFileType.energyBill)
  const energyBillCustomerFile = contract?.energyBillInfo?.energyBillFile
  const energyBillFile = Boolean(energyBillCustomerFile) || Boolean(energyBillDocument)
  const canEditEnergyBill = isFormalized ? energyBillDocument?.status === EProposalFileStatus.rejected : true

  const schema = yup.object().shape({
    login: yup.string().required(),
    password: yup.string().required(),
    address: addressSchema,
    energyBill: energyBillFile ? fileSchema : requiredFileSchema,
    tusdValue: yup.number().required(),
    energyTaxes: yup.number().required(),
    AmountCents: yup.number().required(),
    clientNumber: yup.string().required(),
    dealershipId: yup.string().required(),
    extraPassword: yup.string(),
    distributionTaxes: yup.number().required(),
    installationNumber: yup.string().required().matches(INSTALLATION_NUMBER_REGEX, INSTALLATION_NUMBER_ERROR_MESSAGE),
    invoiceDueDay: yup.number().max(31).min(1).required(),
    avarageConsumption: yup.string().required(),
    tension: yup.string().required(),
    averageEnergyBillValue: yup.number(),
    consumerClassification: yup.string(),
    tariffClassification: yup.string().required(),
    avarageBillAmountCents: yup.number().required(),
    name: yup.string(),
    monthlyConsumption: yup.object(
      monthsLabel.reduce((acc, month) => {
        acc[month] = yup
          .number()
          .transform((value, originalValue) => {
            return originalValue === '' || typeof originalValue === 'undefined' ? null : value
          })
          .nullable()
          .notRequired()
          .integer()
          .moreThan(0)
        return acc
      }, {} as Record<string, any>)
    ),
    previousGenerationBalance:
      yup.number()
        .transform((value, originalValue) =>
          originalValue === '' || originalValue === null || typeof originalValue === 'undefined'
            ? null
            : value
        )
        .min(0),
    ownGenerationAverageKwh:
      yup.number()
        .transform((value, originalValue) =>
          originalValue === '' || originalValue === null || typeof originalValue === 'undefined'
            ? null
            : value
        )
        .min(0)
  })

  const methods = useForm<ConsumerUnitForm>({
    mode: 'onChange',
    resolver: yupResolver(schema)
  })

  useEffect(() => {
    if (dealerships && dealerships.dealershipList.data.length !== 0) {
      const mappedDealerships = dealerships.dealershipList.data.map(item => {
        return {
          label: item.name || '',
          value: item._id
        }
      })

      if (mappedDealerships.length !== 0) {
        const registeredDealershipId = contract?.energyBillInfo?.dealershipId

        setFilteredDealerships(mappedDealerships)
        methods.setValue('dealershipId', registeredDealershipId ?? mappedDealerships[0].value)
      } else {
        methods.setValue('dealershipId', '')
        setFilteredDealerships([defaultOption])
      }
    }

    if (contract) {
      const { energyBillInfo } = contract

      if (energyBillInfo?.address) {
        methods.setValue('address.city', energyBillInfo.address.city)
        methods.setValue('address.complement', energyBillInfo.address.complement || '')
        methods.setValue('address.neighborhood', energyBillInfo.address.neighborhood)
        methods.setValue('address.number', energyBillInfo.address.number)
        methods.setValue('address.state', energyBillInfo.address.state)
        methods.setValue('address.street', energyBillInfo.address.street)
        methods.setValue('address.zipcode', energyBillInfo.address.zipcode)
      }

      methods.setValue('consumerClassification', energyBillInfo?.consumerClassification || '' as EConsumerUnitClassification)
      methods.setValue('tension', energyBillInfo?.tension || '' as EConsumerUnitTension)
      methods.setValue('dealershipId', energyBillInfo?.dealershipId || '')
      methods.setValue('clientNumber', energyBillInfo?.clientNumber || '')
      methods.setValue('installationNumber', energyBillInfo?.installationNumber || '')
      methods.setValue('invoiceDueDay', energyBillInfo?.invoiceDueDay || undefined)
      methods.setValue('tariffClassification', energyBillInfo?.tariffClassification || '' as ETariffClassification)
      methods.setValue('avarageConsumption', energyBillInfo?.avarageConsumption ? String(energyBillInfo?.avarageConsumption) : '')
      methods.setValue('tusdValue', contract?.energyBillInfo?.tusdValue ? transformMoney(contract?.energyBillInfo?.tusdValue, 'toReal') : undefined)
      methods.setValue('AmountCents', contract?.energyBillInfo?.AmountCents ? transformMoney(contract?.energyBillInfo?.AmountCents, 'toReal') : undefined)
      methods.setValue('energyTaxes', contract?.energyBillInfo?.energyTaxes ? transformMoney(contract?.energyBillInfo?.energyTaxes, 'toReal') : undefined)
      methods.setValue('distributionTaxes', contract?.energyBillInfo?.distributionTaxes ? transformMoney(contract?.energyBillInfo?.distributionTaxes, 'toReal') : undefined)
      methods.setValue('avarageBillAmountCents', contract?.energyBillInfo?.avarageBillAmountCents ? transformMoney(contract?.energyBillInfo?.avarageBillAmountCents, 'toReal') : undefined)
      methods.setValue('extraPassword', contract?.dealershipLogin?.extraPassword || '')
      methods.setValue('login', contract?.dealershipLogin?.login || '')
      methods.setValue('password', contract?.dealershipLogin?.password || '')
      methods.setValue('name', contract?.energyBillInfo?.name || '')
      methods.setValue(
        'previousGenerationBalance',
        contract?.previousGenerationBalance !== null && contract?.previousGenerationBalance !== undefined
          ? contract.previousGenerationBalance
          : undefined
      )
      methods.setValue(
        'ownGenerationAverageKwh',
        contract?.ownGenerationAverageKwh !== null && contract?.ownGenerationAverageKwh !== undefined
          ? contract.ownGenerationAverageKwh
          : undefined
      )
    }
  }, [dealerships, methods])

  useEffect(() => {
    methods.setValue('dealershipId', contract?.energyBillInfo?.dealershipId || '')
  }, [filteredDealerships])

  const uploadFileToS3 = async (file: File[]) => {
    setUploadingFile(true)

    try {
      const uploadedFile = await uploadToS3(file)
      setUploadingFile(false)

      return uploadedFile
    } catch (err) {
      setUploadingFile(false)
      toast.error('Houve um problema com um dos arquivos enviados', {
        autoClose: 3000,
        position: 'top-right'
      })
    }
  }

  const handleSubmitInSentStatus = async (formData: ConsumerUnitForm) => {
    if (contract) {
      const {
        address,
        tension,
        tusdValue,
        AmountCents,
        energyBill,
        energyTaxes,
        clientNumber,
        dealershipId,
        distributionTaxes,
        installationNumber,
        invoiceDueDay,
        avarageConsumption,
        tariffClassification,
        consumerClassification,
        avarageBillAmountCents,
        name
      } = formData

      let file: FileModel | null | undefined = null
      const dealershipName = dealerships?.dealershipList.data.find(dealership => dealership._id === dealershipId)?.name

      if (canEditEnergyBill && energyBill && energyBill[0] && energyBill[0].size) {
        file = await uploadFileToS3(energyBill)
      }

      const formattedAddress = address ? {
        ...address,
        zipcode: clearString(address?.zipcode),
        country: 'Brasil',
      } : null

      const hasDealershipLogin = [formData.login, formData.password, formData.extraPassword].some(item => item)

      const params: ContractUpdateEnergyBillInfo = {
        tension,
        name,
        clientNumber,
        dealershipId,
        dealershipName,
        installationNumber,
        invoiceDueDay,
        tariffClassification,
        consumerClassification,
        address: formattedAddress,
        tusdValue: tusdValue ? transformMoney(tusdValue) : 0,
        AmountCents: AmountCents ? transformMoney(AmountCents) : 0,
        energyTaxes: energyTaxes ? transformMoney(energyTaxes) : 0,
        avarageConsumption: Number(onlyNumbers(avarageConsumption)),
        distributionTaxes: distributionTaxes ? transformMoney(distributionTaxes) : 0,
        avarageBillAmountCents: avarageBillAmountCents ? transformMoney(avarageBillAmountCents) : 0,
        contractId: contract._id,
        dealershipLogin: hasDealershipLogin ? {
          login: formData.login,
          password: formData.password,
          extraPassword: formData.extraPassword
        } : null
      }

      const existingContractFile = energyBillDocument?.file
      let energyBillFileInput: ContractFileCreateDto | undefined
      if (file && !isEmpty(file)) {
        energyBillFileInput = {
          key: file.key,
          fileUrl: file.fileUrl,
          fileName: file.fileName,
          storageService: file.storageService as EStorageService
        }
      } else if (
        existingContractFile &&
        existingContractFile.key &&
        existingContractFile.fileUrl &&
        existingContractFile.fileName &&
        existingContractFile.storageService
      ) {
        // Preserva o arquivo existente para evitar limpeza pelo backend
        energyBillFileInput = {
          key: existingContractFile.key as string,
          fileUrl: existingContractFile.fileUrl as string,
          fileName: existingContractFile.fileName as string,
          storageService: existingContractFile.storageService as EStorageService
        }
      }

      const formattedDataWithFile: ContractUpdateEnergyBillInfo = energyBillFileInput
        ? { ...params, energyBillFile: energyBillFileInput }
        : params

      try {
        const { data: updatedContract } = await updateContractEnergyBillInfo({ variables: { params: formattedDataWithFile } })

        if (updatedContract && updatedContract.contractUpdateEnergyBillInfo) {
          const merged: ContractModel = {
            ...(contract as ContractModel),
            ...(updatedContract.contractUpdateEnergyBillInfo as ContractModel),
            // Preserva files (status, sentAt) quando a mutação não retorna esse campo
            files: contract?.files || (updatedContract.contractUpdateEnergyBillInfo as any)?.files || []
          }
          setContract(merged)
        }

        setIsDisabledContractUpdate(true)
        setIsUpdatingContract(false)
        toast.success('Dados atualizados com sucesso!')
      } catch (err) {
        const apolloError = err as ApolloError
        toast.error(mapErrorToMessage(apolloError?.graphQLErrors[0]?.extensions?.code as string, apolloError.message || 'Houve um problema ao atualizar os dados'), {
          autoClose: 3000,
          position: 'top-right'
        })
      }
    }
  }

  const handleSubmit = async (formData: ConsumerUnitForm) => {
    if (contract) {
      const {
        address,
        tension,
        tusdValue,
        AmountCents,
        energyBill,
        energyTaxes,
        clientNumber,
        dealershipId,
        distributionTaxes,
        installationNumber,
        invoiceDueDay,
        avarageConsumption,
        tariffClassification,
        consumerClassification,
        avarageBillAmountCents,
        name
      } = formData

      let file: FileModel | null | undefined = null
      const dealershipName = dealerships?.dealershipList.data.find(dealership => dealership._id === dealershipId)?.name

      if (canEditEnergyBill && energyBill && energyBill[0] && energyBill[0].size) {
        file = await uploadFileToS3(energyBill)
      }

      const formattedAddress = address ? {
        ...address,
        zipcode: clearString(address?.zipcode),
        country: 'Brasil',
      } : null

      const params: ContractAddEnergyBillInfoGraphqlDto = {
        _id: contract._id,
        tension,
        name,
        clientNumber,
        dealershipId,
        dealershipName,
        installationNumber,
        invoiceDueDay,
        tariffClassification,
        consumerClassification,
        address: formattedAddress,
        tusdValue: tusdValue ? transformMoney(tusdValue) : 0,
        AmountCents: AmountCents ? transformMoney(AmountCents) : 0,
        energyTaxes: energyTaxes ? transformMoney(energyTaxes) : 0,
        avarageConsumption: Number(onlyNumbers(avarageConsumption)),
        distributionTaxes: distributionTaxes ? transformMoney(distributionTaxes) : 0,
        avarageBillAmountCents: avarageBillAmountCents ? transformMoney(avarageBillAmountCents) : 0
      }

      const existingContractFile = energyBillDocument?.file
      let energyBillFileInput: ContractFileCreateDto | undefined
      if (file && !isEmpty(file)) {
        energyBillFileInput = {
          key: file.key,
          fileUrl: file.fileUrl,
          fileName: file.fileName,
          storageService: file.storageService as EStorageService
        }
      } else if (
        existingContractFile &&
        existingContractFile.key &&
        existingContractFile.fileUrl &&
        existingContractFile.fileName &&
        existingContractFile.storageService
      ) {
        // Preserva o arquivo existente para evitar limpeza pelo backend
        energyBillFileInput = {
          key: existingContractFile.key as string,
          fileUrl: existingContractFile.fileUrl as string,
          fileName: existingContractFile.fileName as string,
          storageService: existingContractFile.storageService as EStorageService
        }
      }

      const formattedDataWithFile: ContractAddEnergyBillInfoGraphqlDto = energyBillFileInput
        ? { ...params, energyBillFile: energyBillFileInput }
        : params

      const hasDealershipLogin = [formData.login, formData.password, formData.extraPassword].some(item => item)

      try {
        await updateContract({
          variables: {
            params: {
              _id: contract._id,
              previousGenerationBalance: formData.previousGenerationBalance,
              ownGenerationAverageKwh: Number(formData.ownGenerationAverageKwh),
              specialDiscount: contract.specialDiscount ? [...contract.specialDiscount.map(item => {
                return {
                  month: item.month,
                  discountPercent: item.discountPercent
                }
              })] : [],
              dealershipLogin: hasDealershipLogin ? {
                login: formData.login,
                password: formData.password,
                extraPassword: formData.extraPassword
              } : null
            }
          }
        })

        const { data: updatedContract } = await updateEnergyBillInfo({ variables: { params: formattedDataWithFile } })

        if (updatedContract && updatedContract.contractAddEnergyBillInfo) {
          const merged: ContractModel = {
            ...(contract as ContractModel),
            ...(updatedContract.contractAddEnergyBillInfo as ContractModel),
            // Preserva files (status, sentAt) quando a mutação não retorna esse campo
            files: contract?.files || (updatedContract.contractAddEnergyBillInfo as any)?.files || []
          }
          setContract(merged)
        }
        toast.success('Dados atualizados com sucesso!')
      } catch (err) {
        const apolloError = err as ApolloError
        toast.error(mapErrorToMessage(apolloError?.graphQLErrors[0]?.extensions?.code as string, apolloError.message || 'Houve um problema ao atualizar os dados'), {
          autoClose: 3000,
          position: 'top-right'
        })
      }

      return
    }

    toast.error('Houve um problema ao enviar os dados')
  }

  const onSubmit = async (formData: ConsumerUnitForm) => {
    if (isComplete) {
      await handleSubmitInSentStatus(formData)
    } else {
      await handleSubmit(formData)
    }
  }

  const disabledFields = isDisabledContractUpdate && (contract?.status === EContractStatus.active || contract?.status === EContractStatus.sent || contract?.status === EContractStatus.cancellationRequested || contract?.status === EContractStatus.inDeactivation)

  return (
    <FormProvider {...methods}>
      <Form>
        <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 3 }}>
          {contract?.proposalId && <ContractInfo contract={contract} />}

          <Typography variant='h3' fontWeight={500}>Informações básicas</Typography>
          <Grid spacing={3} container>
            <Grid item md={4} xs={12}>
              <Input disabled={disabledFields} name='name' label='Nome (Opcional)' />
            </Grid>
          </Grid>

          <Box display='flex' alignItems='baseline' gap={isMobile ? 1 : 2} flexDirection={isMobile ? 'column' : 'row'}>
            <Typography variant='h3' fontWeight={500} marginTop={'1rem'}>Fatura de energia</Typography>
            <Typography color='grey.400'>Informe os dados e anexe o arquivo da fatura de energia</Typography>
          </Box>

          <DragAndDrop
            // canResend
            name='energyBill'
            $variant='secondary'
            status={isFormalized ? energyBillDocument?.status : undefined}
            disabled={disabledFields || isFormalized && energyBillDocument?.status !== EProposalFileStatus.rejected}
            label='Anexar última fatura de energia'
            fileDescription='(JPG, JPEG, PNG, PDF. Tamanho máximo 15MB)'
            $completed={isFormalized ? Boolean((energyBillDocument as any)?.sentAt) : Boolean(energyBillCustomerFile)}
            fileName={isFormalized ? energyBillDocument?.file?.fileName || undefined : energyBillCustomerFile?.fileName || undefined}
            fileUrl={
              isFormalized
                ? energyBillDocument?.file?.key && `${process.env.VITE_APP_REST_API_URL}files/download/file/${energyBillDocument?.file?.key}`
                || energyBillDocument?.file?.fileUrl || ''
                : (energyBillCustomerFile as ContractFileModel)?.key && `${process.env.VITE_APP_REST_API_URL}files/download/file/${(energyBillCustomerFile as ContractFileModel)?.key}`
                || energyBillCustomerFile?.fileUrl || ''
            }
            sentAt={isFormalized ? (energyBillDocument as any)?.sentAt : undefined}
          />

          <EnergyBillFields loadingDealerships={loadingDealerships} dealerships={filteredDealerships} />
          <Typography variant='h3' fontWeight={500} marginTop={'1rem'}>Outras fontes de geração</Typography>
          <Grid spacing={3} container>
            <Grid item xs={12} md={4}>
              <Input
                disabled={disabledFields}
                name='previousGenerationBalance'
                placeholder='Saldo de geração anterior'
                label='Saldo de geração anterior'
                mask='kwh'
                icons={{
                  end: {
                    element:
                      <Tooltip title='Saldo de geração de energia que o cliente possui antes de assinar com a Desperta.
Esse valor é comum em casos de geração própria ou contratos prévios de GD.'>
                        <IconInfoCircle size={24} color='#3BA1FF' />
                      </Tooltip>
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Input
                disabled={disabledFields}
                name='ownGenerationAverageKwh'
                placeholder='Média de geração própria'
                label='Média de geração própria'
                mask='kwh'
              />
            </Grid>
          </Grid>
          <Typography variant='h3' fontWeight={500} marginTop={'1rem'}>Chaves de acesso da concessionária</Typography>
          <Grid spacing={3} container>
            <Grid item xs={12} md={4}>
              <Input disabled={disabledFields} name='login' label='Login' />
            </Grid>
            <Grid item xs={12} md={4}>
              <Input disabled={disabledFields} name='password' label='Senha' type='password' />
            </Grid>
            <Grid item xs={12} md={4}>
              <Input disabled={disabledFields} name='extraPassword' label='Chave extra' type='password' />
            </Grid>
          </Grid>

          {contract && <ContractAnnotations contract={contract} />}

          <ActionButtons onSubmit={methods.handleSubmit(onSubmit)} loading={updateEnergyBillLoading || uploadingFile || updateContractLoading || updateContractEnergyBillInfoLoading} {...props} />
        </Box>
      </Form>
    </FormProvider>
  )
}
